/**
 * Frontend JWT and User Validation
 * Validates that currentUser has all required fields from JWT
 */

import { ROLES, USER_TYPES } from '../config/constants';

export interface ValidatedUser {
  id: string;
  name: string;
  email: string;
  role: string;
  type: string; // INTERNAL or EXTERNAL
  userType?: string; // backwards compatibility
  empresaId: string | null;
  sucursalId: string | null;
  empresa?: any;
  sucursal?: any;
}

const VALID_ROLES = [ROLES.ADMIN, ROLES.ANALYST, ROLES.CLIENT];
const VALID_TYPES = [USER_TYPES.INTERNAL, USER_TYPES.EXTERNAL];

export function validateCurrentUser(user: any): { valid: boolean; warnings: string[] } {
  const warnings: string[] = [];

  if (!user) {
    warnings.push('currentUser is null or undefined');
    return { valid: false, warnings };
  }

  // Check required fields
  if (!user.id) warnings.push('Missing user.id');
  if (!user.name) warnings.push('Missing user.name');
  if (!user.email) warnings.push('Missing user.email');
  if (!user.role) warnings.push('Missing user.role');
  if (!user.type && !user.userType) warnings.push('Missing user.type/userType');
  if (user.empresaId === undefined) warnings.push('Missing user.empresaId (should be present, even if null)');
  if (user.sucursalId === undefined) warnings.push('Missing user.sucursalId (should be present, even if null)');

  // Validate role
  if (user.role && !VALID_ROLES.includes(user.role)) {
    warnings.push(`Invalid role: ${user.role}. Expected: ${VALID_ROLES.join(', ')}`);
  }

  // Validate type
  const type = user.type || user.userType;
  if (type && !VALID_TYPES.includes(type)) {
    warnings.push(`Invalid type: ${type}. Expected: ${VALID_TYPES.join(', ')}`);
  }

  // Validate empresaId for EXTERNAL users
  if (type === 'EXTERNAL' && !user.empresaId) {
    warnings.push('EXTERNAL users must have empresaId');
  }

  return {
    valid: warnings.length === 0,
    warnings
  };
}

export function normalizeUser(user: any): ValidatedUser {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role,
    type: user.type || user.userType,
    userType: user.type || user.userType, // backwards compatibility
    empresaId: user.empresaId || null,
    sucursalId: user.sucursalId || null,
    empresa: user.empresa,
    sucursal: user.sucursal,
  };
}

/**
 * Show validation warnings in development mode only
 */
export function showDevWarnings(user: any): void {
  if (process.env.NODE_ENV !== 'development') return;

  const validation = validateCurrentUser(user);
  if (!validation.valid) {
    console.warn('⚠️ JWT/User Validation Issues Detected:');
    validation.warnings.forEach(warning => {
      console.warn(`  - ${warning}`);
    });
    console.warn('Current user object:', user);
  }
}
